import { nextTick, watch } from 'vue';
import type {
  // LocationQueryRaw,
  RouteLocationNormalized,
  RouteLocationRaw,
  Router
} from 'vue-router';
import type { RouteKey, RoutePath } from '@elegant-router/types';
import { useAuthStore } from '@/store/modules/auth';
import { useRouteStore } from '@/store/modules/route';
// import { localStg } from '@/utils/storage';
import { useUserStore } from '@/store/modules/user';
import { useSupportStore } from '@/store/modules/support';
import { $t } from '@/locales';
import { getRouteName } from '../elegant/transform';

// Routes that require support agent/admin access
const SUPPORT_AGENT_ROUTES = ['support_all-tickets'];
/**
 * create route guard
 *
 * @param router router instance
 */
// export function createRouteGuard(router: Router) {
//   router.beforeEach(async (to, from, next) => {
//     const location = await initRoute(to);

//     if (location) {
//       next(location);
//       return;
//     }

//     const authStore = useAuthStore();
//     const userStore = useUserStore();
//     const isLogin = userStore.isUserAuthenticated();

//     const rootRoute: RouteKey = 'root';
//     const loginRoute: RouteKey = 'login';
//     const noAuthorizationRoute: RouteKey = '403';

//     const needLogin = !to.meta.constant;
//     const routeRoles = to.meta.roles || [];

//     const hasRole = authStore.userInfo.roles.some(role => routeRoles.includes(role));

//     const hasAuth = authStore.isStaticSuper || !routeRoles.length || hasRole;

//     const routeSwitches: CommonType.StrategicPattern[] = [
//       // if it is login route when logged in, then switch to the root page
//       {
//         condition: isLogin && to.name === loginRoute,
//         callback: () => {
//           next({ name: rootRoute });
//         }
//       },
//       // if is is constant route, then it is allowed to access directly
//       {
//         condition: !needLogin,
//         callback: () => {
//           handleRouteSwitch(to, from, next);
//         }
//       },
//       // if the route need login but the user is not logged in, then switch to the login page
//       {
//         condition: !isLogin && needLogin,
//         callback: () => {
//           next({ name: loginRoute, query: { redirect: to.fullPath } });
//         }
//       },
//       // if the user is logged in and has authorization, then it is allowed to access
//       {
//         condition: isLogin && needLogin && hasAuth,
//         callback: () => {
//           handleRouteSwitch(to, from, next);
//         }
//       },
//       // if the user is logged in but does not have authorization, then switch to the 403 page
//       {
//         condition: isLogin && needLogin && !hasAuth,
//         callback: () => {
//           next({ name: noAuthorizationRoute });
//         }
//       }
//     ];

//     routeSwitches.some(({ condition, callback }) => {
//       if (condition) {
//         callback();
//       }

//       return condition;
//     });
//   });
// }

export function createRouteGuard(router: Router) {
  const userStore = useUserStore();
  const routeStore = useRouteStore();

  // Watch for authentication status changes
  watch(
    () => userStore.isUserAuthenticated(),
    async newIsAuthenticated => {
      if (newIsAuthenticated) {
        // Re-initialize auth routes when user becomes authenticated
        await routeStore.initAuthRoute();
        // Optionally redirect to the intended route if there was one
        const redirect = router.currentRoute.value.query.redirect as string;
        if (redirect) {
          router.push(redirect);
        }

        return;
      }

      // Clear auth routes when user becomes unauthenticated.
      // Only redirect to login if not in guest mode.
      if (!userStore.isInGuestMode()) {
        routeStore.$reset(); // Assuming your store has a reset method
        router.push({ name: 'login' });
      }
    }
  );

  router.beforeEach(async (to, from) => {
    const location = await initRoute(to, router);

    if (location) {
      return location;
    }

    // A user is logged in if the async profile flag is set OR a valid token
    // exists. The token (authStore.isLogin) is available synchronously at boot,
    // whereas `isUserAuthenticated()` is only set once the async ping/getUserInfo
    // resolves. Without the token check, a logged-in user refreshing a support
    // route would momentarily be treated as a guest and shown the LoginOverlay.
    const isLogin = userStore.isUserAuthenticated() || useAuthStore().isLogin;

    const rootRoute: RouteKey = 'root';
    const loginRoute: RouteKey = 'login';

    // A route prompts for login when it is not a public/constant route, OR when
    // it is explicitly flagged `guestViewable` (constant + public UI that still
    // wants the LoginOverlay, e.g. the user-facing support views). This lets an
    // unauthenticated visitor deep-link a ticket and see the real support UI
    // with the standard overlay instead of a bare not-found shell.
    const needLogin = (!to.meta.constant || to.meta.guestViewable === true) && to.path !== '/';

    // Check if this is a support agent route
    const isSupportAgentRoute = SUPPORT_AGENT_ROUTES.includes(to.name as string);

    // Handle support agent route access check FIRST (async operation).
    //
    // We intentionally never redirect to a `/403` page here. Two very
    // different situations previously collapsed into the same "403" redirect:
    //   1. A genuine permission failure (the profile loaded fine but the user
    //      is not a support agent/admin).
    //   2. A transient backend failure (e.g. a 502 Bad Gateway while loading
    //      the profile) that left `userProfile` null and therefore made
    //      `hasAgentAccess` falsy.
    //
    // Bouncing the user to an access-denied page in case (2) is misleading and
    // there is no dedicated 403 surface we want to expose. Instead we surface a
    // toast that describes what happened and send the user to the support
    // dashboard, which has its own retryable error handling.
    if (isSupportAgentRoute && isLogin) {
      const supportStore = useSupportStore();
      const supportHomeRoute: RouteKey = 'support_dashboard';

      // Ensure the profile is loaded. getUserProfile() swallows API/network
      // errors internally and resolves to null, so a null result here means the
      // profile could not be loaded (transient), while a loaded profile that
      // lacks agent access is a genuine permission denial.
      if (!supportStore.userProfile) {
        await supportStore.getUserProfile();
        await nextTick(() => {});
      }

      const profileLoaded = Boolean(supportStore.userProfile);

      if (!profileLoaded) {
        // Transient failure: could not load profile. Inform the user and route
        // them to a page that can recover, rather than a dead-end 403.
        window.$message?.error?.($t('support.messages.profileUnavailable'));
        await nextTick(() => {});
        return { name: supportHomeRoute };
      }

      if (!supportStore.hasAgentAccess) {
        // Genuine permission denial: the profile loaded but the user is not a
        // support agent/admin. Show an access message instead of a 403 page.
        window.$message?.warning?.($t('support.accessDenied.agentRequired'));
        await nextTick(() => {});
        return { name: supportHomeRoute };
      }

      // If user has agent access, continue to handleRouteSwitch below
      await nextTick(() => {});
      return handleRouteSwitch(to, from);
    }

    // Rest of the route guard logic - synchronous patterns

    // Authenticated users accessing login page -> redirect to home
    if (isLogin && to.name === loginRoute) {
      return { name: rootRoute };
    }

    // For unauthenticated users accessing protected routes, set guest mode
    if (!isLogin && needLogin) {
      // Set guest mode for unauthenticated users
      userStore.setGuestMode(true);
      userStore.setGuestSessionStart();

      const authStore = useAuthStore();

      // Always remember the latest target (including its query, e.g. the
      // `?ticket=TK-...` deep link) so the user returns here after logging in.
      authStore.setOriginalRoute(to.fullPath);

      // Deterministically ensure the overlay is visible for a guest on a
      // login-required route. Setting it `true` is idempotent, so it survives
      // the extra navigations that happen on a fresh load / refresh (constant-
      // route stale redirect + the view's own `router.replace` query
      // normalization) without ever flashing closed. We intentionally do NOT
      // toggle based on `from`/`to` here — only an explicit dismiss or a
      // successful login should hide it.
      authStore.setOverlayVisibility(true);

      // Allow access to the route
      return handleRouteSwitch(to, from);
    }

    // All other cases - allow access
    return handleRouteSwitch(to, from);
  });
}

/**
 * initialize route
 *
 * @param to to route
 */
async function initRoute(to: RouteLocationNormalized, router: Router): Promise<RouteLocationRaw | null> {
  const routeStore = useRouteStore();

  const notFoundRoute: RouteKey = 'not-found';
  const isNotFoundRoute = to.name === notFoundRoute;

  // if the constant route is not initialized, then initialize the constant route
  // If routes aren't initialized yet, initialize them
  if (!routeStore.isInitConstantRoute) {
    await routeStore.initConstantRoute();
  }

  // if the route is the constant route but is not the "not-found" route, then it is allowed to access.
  if (to.meta.constant && !isNotFoundRoute) {
    return null;
  }

  // the auth route is initialized
  // it is not the "not-found" route, then it is allowed to access
  if (routeStore.isInitAuthRoute && !isNotFoundRoute) {
    return null;
  }
  // it is captured by the "not-found" route, then check whether the route exists
  if (routeStore.isInitAuthRoute && isNotFoundRoute) {
    const isConstantRoute = await routeStore.getIsConstantRouteExist(to.path as RoutePath);

    if (isConstantRoute) {
      await routeStore.restoreStaticRoutes();

      return createStaleRouteRedirect(to, router);
    }

    const exist = await routeStore.getIsAuthRouteExist(to.path as RoutePath);

    if (exist) {
      // The target route genuinely exists in the route config, but the live
      // vue-router record is missing (e.g. auth routes were reset after a failed
      // request such as a 502 during app boot). Re-register the auth routes and
      // redirect to the original target instead of incorrectly showing a 403.
      // A 403 should only be shown for genuine permission failures, not for a
      // transient missing-route-record race.
      await routeStore.initAuthRoute();

      return createStaleRouteRedirect(to, router);
    }

    return null;
  }

  // if the auth route is not initialized, then initialize the auth route
  // const isLogin = Boolean(localStg.get('token'));
  // checkAuthentication();
  // initialize the auth route requires the user to be logged in, if not, redirect to the login page
  // const userStore = useUserStore();
  // const isLogin = userStore.isUserAuthenticated();
  // if (!isLogin) {
  //   const loginRoute: RouteKey = 'login';
  //   const redirect = to.fullPath;

  //   const query: LocationQueryRaw = to.name !== loginRoute ? { redirect } : {};

  //   const location: RouteLocationRaw = {
  //     name: loginRoute,
  //     query
  //   };

  //   return location;
  // }

  // initialize the auth route
  await routeStore.initAuthRoute();

  // the route is captured by the "not-found" route because the auth route is not initialized
  // after the auth route is initialized, redirect to the original route
  if (isNotFoundRoute) {
    return createStaleRouteRedirect(to, router);
  }

  return null;
}

function createStaleRouteRedirect(to: RouteLocationNormalized, router: Router): RouteLocationRaw | null {
  const notFoundRoute: RouteKey = 'not-found';
  const routeName = getRouteName(to.path as RoutePath);

  if (routeName && router.hasRoute(routeName)) {
    return {
      name: routeName,
      replace: true,
      query: to.query,
      hash: to.hash
    };
  }

  const resolved = router.resolve({
    path: to.path,
    query: to.query,
    hash: to.hash
  });

  if (resolved.matched.length > 0 && resolved.name !== notFoundRoute) {
    return {
      path: to.path,
      replace: true,
      query: to.query,
      hash: to.hash
    };
  }

  return null;
}

function handleRouteSwitch(to: RouteLocationNormalized, from: RouteLocationNormalized): RouteLocationRaw | undefined {
  // route with href
  if (to.meta.href) {
    window.open(to.meta.href, '_blank');

    return {
      path: from.fullPath,
      replace: true,
      query: from.query,
      hash: to.hash
    };
  }

  // returning nothing (undefined) allows the navigation to proceed
  return undefined;
}
